import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  forceX,
  forceY,
} from 'd3-force';
import type { Simulation, SimulationNodeDatum } from 'd3-force';
import {
  IDENTITY_VIEW,
  edgeWidth,
  fitRadius,
  fitView,
  linkArc,
  linkCount,
  selfArc,
  siteColor,
  siteKey,
  wrapLabel,
} from './siteGraph';
import type { Segment } from './siteGraph';
import { columnOf, isPairEmpty } from './sitePairing';
import type { DockPair } from './sitePairing';
import type { SiteGraph, SiteNode } from '../../types';

/**
 * Sites as a force-directed graph: drag a node and it STAYS where you put it.
 *
 * d3-force runs the physics; React renders every element. The simulation
 * mutates its own node objects on each tick, so a tick handler copies the
 * positions into state and React draws from that — no `d3-selection`, no
 * enter/exit, and drag state lives in one place instead of being split between
 * React and a DOM node's `__data__`.
 *
 * STICKY, like `@d3/sticky-force-layout`: `fx`/`fy` are set on drag and KEPT on
 * release rather than cleared. An unpinned simulation re-settles somewhere
 * different every time the panel opens, which makes the picture unlearnable —
 * pinning turns the physics into a first draft you correct.
 *
 * Positions persist per map in sessionStorage for the same reason. Switching to
 * the Tree lens and back should not re-roll the layout you just arranged.
 */

const W = 760;
const H = 520;
// The arrowhead is deliberately large relative to the lines. It has to read
// against a node's fill, its 3px ring and a dashed selection halo, all of which
// sit exactly where it lands.
const ARROW = 15;
/** Pointer travel, in screen px, above which a press is a drag and not a click.
 *  Small enough that a deliberate nudge still pins; large enough that a click
 *  on a trackpad does not. */
const CLICK_SLOP = 4;

interface Node extends SimulationNodeDatum {
  key: string;
  site: SiteNode;
  colorIndex: number;
  r: number;
  lines: string[];
}

interface Link {
  source: string | Node;
  target: string | Node;
  count: number;
}

interface Props {
  graph: SiteGraph;
  mapId: number;
  selected: Segment | null;
  onSelect: (segment: Segment) => void;
  /** A node now has TWO gestures. See `press` for how they are told apart. */
  onSelectSite: (siteId: number | null) => void;
  /** Which sites are open in the dock below, so the graph says where those
   *  page lists came from. */
  pair: DockPair;
  /** Site keys fed by the subject chip the user is holding, and that chip's own
   *  colour. Same hue on the chip and the halo is the whole point: it answers
   *  "which of these nodes is that chip" without a word of explanation. */
  litSites?: Set<string> | null;
  litColor?: string;
}

interface Frame {
  nodes: Node[];
  links: Link[];
}

export default function SiteMap({
  graph,
  mapId,
  selected,
  onSelect,
  onSelectSite,
  pair,
  litSites,
  litColor,
}: Props) {
  const [frame, setFrame] = useState<Frame>({ nodes: [], links: [] });
  const [dragging, setDragging] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const [view, setView] = useState(IDENTITY_VIEW);
  const sim = useRef<Simulation<Node, undefined> | null>(null);
  /**
   * Where a press on a node started, so a CLICK can be told from a DRAG.
   *
   * Dragging a node pins it; clicking one opens its pages. Both begin with
   * pointerdown on the same circle, so without a movement threshold every
   * attempt to open a site nudges it a pixel and pins it instead — and the
   * pages never open.
   */
  const press = useRef<{ key: string; x: number; y: number } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const pan = useRef<{ x: number; y: number; vx: number; vy: number } | null>(null);
  const pinnedBefore = useRef(false);
  /**
   * Has the user taken over the view?
   *
   * Until they do, every tick refits, so the graph is never off screen while it
   * settles. The moment they zoom or pan, that stops — a view that keeps
   * snapping back is worse than one that started wrong, and `Fit` hands control
   * back when they want it.
   */
  const touched = useRef(false);

  const pinKey = `linkmap:${mapId}:sitepins`;

  const readPins = useCallback((): Record<string, [number, number]> => {
    try {
      return JSON.parse(sessionStorage.getItem(pinKey) || '{}');
    } catch {
      // A corrupt pin store must not take the whole view down with it.
      return {};
    }
  }, [pinKey]);

  useEffect(() => {
    const pins = readPins();
    const nodes: Node[] = graph.sites.map((site, i) => {
      const lines = wrapLabel(site.name);
      const pin = pins[siteKey(site.site_id)];
      return {
        key: siteKey(site.site_id),
        site,
        colorIndex: i,
        r: fitRadius(lines, site.page_count),
        lines,
        ...(pin ? { x: pin[0], y: pin[1], fx: pin[0], fy: pin[1] } : {}),
      };
    });
    const links: Link[] = graph.flows
      // Self-links are drawn as a loop on the node. Handing one to `forceLink`
      // makes a spring from a node to itself, whose length is always zero and
      // whose force is therefore undefined — d3 produces NaN and every node
      // disappears.
      .filter((f) => f.from_site_id !== f.to_site_id)
      .map((f) => ({
        source: siteKey(f.from_site_id),
        target: siteKey(f.to_site_id),
        count: f.count,
      }));

    const simulation = forceSimulation<Node>(nodes)
      .force(
        'link',
        forceLink<Node, Link>(links)
          .id((d) => d.key)
          .distance(190)
          .strength(0.55),
      )
      .force('charge', forceManyBody().strength(-900))
      // Collision on the node's own radius plus its label gap, so nodes never
      // overlap however hard charge and link fight each other.
      .force('collide', forceCollide<Node>((d) => d.r + 14).strength(0.9))
      .force('center', forceCenter(W / 2, H / 2))
      // Weak pull to the middle: without it a disconnected site — and there are
      // usually several — drifts off the canvas forever, since nothing but
      // repulsion acts on it.
      .force('x', forceX(W / 2).strength(0.05))
      .force('y', forceY(H / 2).strength(0.05));

    simulation.on('tick', () => {
      const placed = [...simulation.nodes()];
      setFrame({ nodes: placed, links });
      // Not while a node is under the pointer: `toLocal` reads the view to turn
      // screen coordinates into graph ones, so moving the view mid-drag would
      // slide the node out from under the cursor that is holding it.
      if (!touched.current && press.current === null) {
        setView(fitView(placed, W, H));
      }
    });

    sim.current = simulation;
    return () => {
      simulation.stop();
    };
  }, [graph, readPins]);

  const savePins = useCallback(() => {
    const pins: Record<string, [number, number]> = {};
    for (const n of sim.current?.nodes() ?? []) {
      if (n.fx != null && n.fy != null) pins[n.key] = [n.fx, n.fy];
    }
    try {
      sessionStorage.setItem(pinKey, JSON.stringify(pins));
    } catch {
      // Private mode, quota — the graph still works, it just forgets.
    }
  }, [pinKey]);

  /** SVG user units under the pointer, undoing pan and zoom. */
  const toLocal = (e: { clientX: number; clientY: number }) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const sx = (e.clientX - rect.left) * (W / rect.width);
    const sy = (e.clientY - rect.top) * (H / rect.height);
    return {
      x: (sx - W / 2) / view.k + W / 2 - view.x,
      y: (sy - H / 2) / view.k + H / 2 - view.y,
    };
  };

  // Both ends of the range: width is relative to the lightest edge, not to
  // zero, so a map whose counts are all the same draws thin rather than fat.
  const [minFlow, maxFlow] = useMemo(() => {
    if (!graph.flows.length) return [0, 0];
    const counts = graph.flows.map((f) => f.count);
    return [Math.min(...counts), Math.max(...counts)];
  }, [graph.flows]);

  const nodeByKey = useMemo(() => {
    const m = new Map<string, Node>();
    for (const n of frame.nodes) m.set(n.key, n);
    return m;
  }, [frame.nodes]);

  const anyPinned = frame.nodes.some((n) => n.fx != null);
  const dim = (key: string) => hover !== null && hover !== key;

  const release = () => {
    for (const n of sim.current?.nodes() ?? []) {
      n.fx = null;
      n.fy = null;
    }
    try {
      sessionStorage.removeItem(pinKey);
    } catch {
      /* nothing to do */
    }
    touched.current = false;
    sim.current?.alpha(0.8).restart();
  };

  return (
    <div className="relative">
      <div className="absolute top-2 right-2 z-10 flex gap-1">
        {anyPinned && (
          <button
            onClick={release}
            className="px-2 h-7 rounded border border-gray-300 dark:border-gray-600 bg-white/90 dark:bg-gray-900/90 text-[11px] text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            title="Unpin every site and let the layout settle again"
          >
            Re-layout
          </button>
        )}
        <button
          onClick={() => {
            touched.current = false;
            setView(fitView(frame.nodes, W, H));
          }}
          className="px-2 h-7 rounded border border-gray-300 dark:border-gray-600 bg-white/90 dark:bg-gray-900/90 text-[11px] text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          title="Zoom to show every site, and keep it that way until you zoom or pan"
        >
          Fit
        </button>
        <button
          onClick={() => {
            touched.current = true;
            setView((v) => ({ ...v, k: Math.min(3, v.k * 1.25) }));
          }}
          className="w-7 h-7 rounded border border-gray-300 dark:border-gray-600 bg-white/90 dark:bg-gray-900/90 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          aria-label="Zoom in"
        >
          +
        </button>
        <button
          onClick={() => {
            touched.current = true;
            setView((v) => ({ ...v, k: Math.max(0.4, v.k / 1.25) }));
          }}
          className="w-7 h-7 rounded border border-gray-300 dark:border-gray-600 bg-white/90 dark:bg-gray-900/90 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          aria-label="Zoom out"
        >
          −
        </button>
      </div>

      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className={`w-full select-none touch-none ${
          dragging ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        role="img"
        aria-label="Links between your sites"
        onWheel={(e) => {
          touched.current = true;
          const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
          setView((v) => ({ ...v, k: Math.min(3, Math.max(0.4, v.k * factor)) }));
        }}
        onPointerDown={(e) => {
          if (dragging) return;
          pan.current = { x: e.clientX, y: e.clientY, vx: view.x, vy: view.y };
        }}
        onPointerMove={(e) => {
          if (dragging) {
            const node = sim.current?.nodes().find((n) => n.key === dragging);
            if (!node) return;
            const p = toLocal(e);
            node.fx = p.x;
            node.fy = p.y;
            // `alphaTarget` rather than `restart`: it keeps the simulation warm
            // for as long as the drag lasts, so the neighbours move WITH the
            // node instead of snapping when you let go.
            sim.current?.alphaTarget(0.3).restart();
            return;
          }
          const d = pan.current;
          if (!d) return;
          touched.current = true;
          setView((v) => ({
            ...v,
            x: d.vx + (e.clientX - d.x) / v.k,
            y: d.vy + (e.clientY - d.y) / v.k,
          }));
        }}
        onPointerUp={(e) => {
          if (dragging) {
            const started = press.current;
            const moved =
              started === null ||
              Math.hypot(e.clientX - started.x, e.clientY - started.y) > CLICK_SLOP;
            sim.current?.alphaTarget(0);
            if (moved) {
              // STICKY: fx/fy are deliberately NOT cleared here.
              savePins();
            } else if (started) {
              // A press that never moved is a click. Unpin the node again so an
              // ordinary click does not silently freeze it.
              const node = sim.current?.nodes().find((n) => n.key === started.key);
              if (node && !pinnedBefore.current) {
                node.fx = null;
                node.fy = null;
              }
              const site = graph.sites.find((s) => siteKey(s.site_id) === started.key);
              if (site) onSelectSite(site.site_id);
            }
            setDragging(null);
          }
          press.current = null;
          pan.current = null;
        }}
        onPointerLeave={() => {
          // Leaving mid-drag is a drag that ended, never a click — the pointer
          // has by definition travelled.
          if (dragging) {
            sim.current?.alphaTarget(0);
            savePins();
            setDragging(null);
          }
          press.current = null;
          pan.current = null;
        }}
      >
        <defs>
          {graph.sites.map((site, i) => (
            <marker
              key={siteKey(site.site_id)}
              id={`sm-arrow-${mapId}-${siteKey(site.site_id)}`}
              viewBox="0 0 10 8"
              // A narrow, long head reads as a direction; a squat one reads as
              // a blob at the end of a line.
              refX="8"
              refY="4"
              markerWidth={ARROW}
              markerHeight={ARROW * 0.8}
              // WITHOUT THIS the marker scales with the path's stroke-width,
              // and a heavy edge grows an arrowhead bigger than the node it
              // points at. It is the default, and it looks like a bug in the
              // data rather than a unit.
              markerUnits="userSpaceOnUse"
              orient="auto-start-reverse"
            >
              <path d="M0,0 L10,4 L0,8 L2.5,4 z" fill={siteColor(i)} />
            </marker>
          ))}
        </defs>

        <g
          transform={`translate(${W / 2},${H / 2}) scale(${view.k}) translate(${
            -W / 2 + view.x
          },${-H / 2 + view.y})`}
        >
          {graph.flows.map((flow) => {
            const from = nodeByKey.get(siteKey(flow.from_site_id));
            const to = nodeByKey.get(siteKey(flow.to_site_id));
            if (!from || !to || from.x == null || to.x == null) return null;

            const self = flow.from_site_id === flow.to_site_id;
            const a = { x: from.x, y: from.y ?? 0, r: from.r };
            const b = { x: to.x, y: to.y ?? 0, r: to.r };
            const geo = self ? selfArc(a) : linkArc(a, b, ARROW);
            const color = siteColor(from.colorIndex);
            const picked =
              selected?.fromSiteId === flow.from_site_id &&
              selected?.toSiteId === flow.to_site_id;
            const faded = dim(from.key) && dim(to.key);

            return (
              <g
                key={`${from.key}->${to.key}`}
                className="cursor-pointer"
                opacity={faded ? 0.12 : 1}
                onClick={() =>
                  onSelect({
                    fromSiteId: flow.from_site_id,
                    toSiteId: flow.to_site_id,
                    fromName: from.site.name,
                    toName: to.site.name,
                    count: flow.count,
                  })
                }
              >
                {/* A 2px line is a 2px click target. */}
                <path d={geo.d} fill="none" stroke="transparent" strokeWidth={18} />
                <path
                  d={geo.d}
                  fill="none"
                  stroke={color}
                  strokeWidth={edgeWidth(flow.count, minFlow, maxFlow)}
                  markerEnd={`url(#sm-arrow-${mapId}-${siteKey(flow.from_site_id)})`}
                  opacity={picked ? 1 : 0.7}
                />
                <g transform={`translate(${geo.labelX},${geo.labelY})`}>
                  <rect
                    x={-22}
                    y={-9}
                    width={44}
                    height={18}
                    rx={9}
                    className="fill-white dark:fill-gray-800"
                    stroke={color}
                    strokeWidth={picked ? 2 : 1}
                  />
                  <text
                    x={-5}
                    textAnchor="middle"
                    dy="0.34em"
                    fontSize={11}
                    fontWeight={600}
                    className="fill-gray-700 dark:fill-gray-200"
                  >
                    {flow.count}
                  </text>
                  {/* THE DIRECTION, out in the open. An arrowhead at the far
                      end has to be read against a node's fill, its ring and a
                      selection halo, all of which sit exactly where it lands.
                      This one is on the count — where the eye already is — so
                      which way an edge runs never needs hunting for. */}
                  <path
                    d="M0,-4.5 L7,0 L0,4.5 L1.8,0 z"
                    fill={color}
                    transform={`translate(6,0) rotate(${geo.angle})`}
                  />
                </g>
                <title>
                  {from.site.name} → {to.site.name}: {linkCount(flow.count)}
                </title>
              </g>
            );
          })}

          {frame.nodes.map((n) => {
            if (n.x == null || n.y == null) return null;
            const color = siteColor(n.colorIndex);
            const pinned = n.fx != null;
            const top = -((n.lines.length - 1) * 13) / 2 - 4;
            const column = columnOf(pair, n.site.site_id);
            // Hover still wins — it is the momentary thing. Otherwise, once a
            // pair is open, everything that is not part of it recedes, so the
            // two lists below have a visible source.
            const lit = litSites?.has(n.key) ?? false;
            const faded =
              !lit && (dim(n.key) || (hover === null && !isPairEmpty(pair) && !column));
            return (
              <g
                key={n.key}
                transform={`translate(${n.x},${n.y})`}
                opacity={faded ? 0.28 : 1}
                className="cursor-grab active:cursor-grabbing"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  (e.target as Element).setPointerCapture?.(e.pointerId);
                  setDragging(n.key);
                  press.current = { key: n.key, x: e.clientX, y: e.clientY };
                  const node = sim.current?.nodes().find((x) => x.key === n.key);
                  // Remembered so a plain click on an ALREADY-pinned node does
                  // not unpin it as a side effect of being clicked.
                  pinnedBefore.current = node?.fx != null;
                  if (node) {
                    node.fx = node.x;
                    node.fy = node.y;
                  }
                }}
                onMouseEnter={() => setHover(n.key)}
                onMouseLeave={() => setHover(null)}
              >
                {litSites?.has(n.key) && (
                  // Drawn FIRST so it sits behind the node, and wide and faint
                  // so it reads as a glow rather than a second selection ring —
                  // the dashed LEFT/RIGHT ring means something else and the two
                  // have to stay distinguishable when both are on.
                  <circle
                    r={n.r + 11}
                    fill="none"
                    stroke={litColor ?? '#6366f1'}
                    strokeWidth={10}
                    opacity={0.3}
                  />
                )}
                {column && (
                  // A ring OUTSIDE the node rather than a thicker border on it:
                  // the border already carries the site colour, and thickening
                  // it reads as a bigger site rather than a chosen one.
                  <circle
                    r={n.r + 6}
                    fill="none"
                    stroke={color}
                    strokeWidth={2}
                    strokeDasharray="4 3"
                    opacity={0.9}
                  />
                )}
                <circle
                  r={n.r}
                  fill={color}
                  fillOpacity={column ? 0.34 : 0.16}
                  stroke={color}
                  strokeWidth={column ? 3 : 2}
                />
                {column && (
                  // WHICH column, not just "selected". With the same pages
                  // possibly on both sides, a node has to be able to say it is
                  // both — otherwise it reads as half-chosen.
                  <g transform={`translate(0,${-n.r - 13})`}>
                    <rect
                      x={column === 'both' ? -22 : -17}
                      y={-8}
                      width={column === 'both' ? 44 : 34}
                      height={15}
                      rx={7.5}
                      fill={color}
                    />
                    <text
                      textAnchor="middle"
                      dy="0.32em"
                      fontSize={9}
                      fontWeight={700}
                      fill="white"
                    >
                      {column === 'both' ? 'BOTH' : column.toUpperCase()}
                    </text>
                  </g>
                )}
                {n.lines.map((line, i) => (
                  <text
                    key={line + i}
                    y={top + i * 13}
                    textAnchor="middle"
                    dy="0.34em"
                    fontSize={11}
                    fontWeight={600}
                    className="fill-gray-800 dark:fill-gray-100"
                  >
                    {line}
                  </text>
                ))}
                <text
                  y={top + n.lines.length * 13 + 3}
                  textAnchor="middle"
                  dy="0.34em"
                  fontSize={10}
                  className="fill-gray-500 dark:fill-gray-400"
                >
                  {n.site.page_count}p · {n.site.outbound}↑ {n.site.inbound}↓
                </text>
                {pinned && (
                  // Says WHY this node is not moving. Without it a pinned node
                  // during a re-layout reads as a stuck simulation.
                  <circle
                    cx={n.r * 0.68}
                    cy={-n.r * 0.68}
                    r={3}
                    fill={color}
                    stroke="white"
                    strokeWidth={1}
                  />
                )}
                <title>
                  {n.site.name} - {n.site.page_count} pages, {n.site.outbound} out,{' '}
                  {n.site.inbound} in{pinned ? ' (pinned)' : ''}
                </title>
              </g>
            );
          })}
        </g>
      </svg>

      <p className="mt-1 px-1 text-xs text-gray-400 dark:text-gray-500">
        Click a site for its pages. Drag one to pin it. Scroll to zoom, drag the
        background to pan.
      </p>
    </div>
  );
}
